/**
 * Custom Views Service
 * User-specific custom ticket views with advanced filtering
 */

import api from '@/lib/api';

export interface FilterCondition {
  field: string;
  operator: string;
  value: any;
}

export interface ConditionGroup {
  conditions: FilterCondition[];
}

export interface CustomViewFilterConfig {
  filters: FilterCondition[];
  groups?: ConditionGroup[];
  groupLogic?: 'AND' | 'OR';
}

export interface CustomViewSortConfig {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export interface CustomView {
  id: string;
  user_id: string;
  name: string;
  icon_name: string;
  filter_config: CustomViewFilterConfig;
  sort_config: CustomViewSortConfig;
  is_default: boolean;
  position: number;
  created_at: string;
  updated_at: string;
  // Sharing fields
  is_owner: boolean;
  access_type: 'owned' | 'shared' | 'all';
  owner_first_name?: string;
  owner_last_name?: string;
}

export interface ViewShareUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export interface ViewShare {
  id: string;
  shared_with_user_id: string | null;
  shared_with_all: boolean;
  created_at: string;
  user: ViewShareUser | null;
}

export interface ViewSharesResponse {
  success: boolean;
  data: {
    shares: ViewShare[];
  };
}

export interface UpdateViewSharesData {
  userIds?: string[];
  shareWithAll?: boolean;
}

export interface CreateCustomViewData {
  name: string;
  iconName?: string;
  filterConfig: CustomViewFilterConfig;
  sortConfig?: CustomViewSortConfig;
  position?: number;
}

export interface UpdateCustomViewData {
  name?: string;
  iconName?: string;
  filterConfig?: CustomViewFilterConfig;
  sortConfig?: CustomViewSortConfig;
  position?: number;
}

export interface CustomViewsResponse {
  success: boolean;
  data: {
    views: CustomView[];
    total: number;
  };
}

export interface CustomViewResponse {
  success: boolean;
  data: {
    view: CustomView;
  };
  message?: string;
}

export interface ReorderView {
  id: string;
  position: number;
}

class CustomViewsService {
  /**
   * Get all custom views for current user
   */
  async getCustomViews(): Promise<CustomViewsResponse> {
    const response = await api.get<CustomViewsResponse>('/custom-views');
    return response.data;
  }

  /**
   * Get a specific custom view
   */
  async getCustomView(id: string): Promise<CustomViewResponse> {
    const response = await api.get<CustomViewResponse>(`/custom-views/${id}`);
    return response.data;
  }

  /**
   * Create a new custom view
   */
  async createCustomView(data: CreateCustomViewData): Promise<CustomViewResponse> {
    const response = await api.post<CustomViewResponse>('/custom-views', data);
    return response.data;
  }

  /**
   * Update a custom view
   */
  async updateCustomView(id: string, data: UpdateCustomViewData): Promise<CustomViewResponse> {
    const response = await api.put<CustomViewResponse>(`/custom-views/${id}`, data);
    return response.data;
  }

  /**
   * Delete a custom view
   */
  async deleteCustomView(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/custom-views/${id}`);
    return response.data;
  }

  /**
   * Reorder custom views
   */
  async reorderCustomViews(views: ReorderView[]): Promise<{ success: boolean; message: string }> {
    const response = await api.put('/custom-views/reorder/batch', { views });
    return response.data;
  }

  /**
   * Get shares for a custom view (owner only)
   */
  async getViewShares(viewId: string): Promise<ViewSharesResponse> {
    const response = await api.get<ViewSharesResponse>(`/custom-views/${viewId}/shares`);
    return response.data;
  }

  /**
   * Update shares for a custom view (owner only)
   */
  async updateViewShares(viewId: string, data: UpdateViewSharesData): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/custom-views/${viewId}/shares`, data);
    return response.data;
  }

  /**
   * Remove a specific share (owner only)
   */
  async removeShare(viewId: string, shareId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/custom-views/${viewId}/shares/${shareId}`);
    return response.data;
  }

  /**
   * Build URL query string from filter config
   * Supports both flat filters and grouped filters with AND/OR logic
   */
  buildQueryString(filterConfig: CustomViewFilterConfig, sortConfig?: CustomViewSortConfig): string {
    const params = new URLSearchParams();

    // If we have groups with OR logic, send as JSON for backend processing
    if (filterConfig.groups && filterConfig.groups.length > 1 && filterConfig.groupLogic === 'OR') {
      params.set('filterGroups', JSON.stringify({
        groups: filterConfig.groups,
        groupLogic: filterConfig.groupLogic
      }));

      // Add sort config
      if (sortConfig) {
        if (sortConfig.sortBy && sortConfig.sortBy !== 'created_at') {
          params.set('sortBy', sortConfig.sortBy);
        }
        if (sortConfig.sortOrder && sortConfig.sortOrder !== 'desc') {
          params.set('sortOrder', sortConfig.sortOrder);
        }
      }

      return params.toString();
    }

    // For single group or AND logic, use flat filter approach (backward compatible)
    // First, group filters by field+operator to merge values
    const groupedFilters: Record<string, any[]> = {};
    filterConfig.filters?.forEach((filter) => {
      const key = `${filter.field}:${filter.operator}`;
      if (!groupedFilters[key]) {
        groupedFilters[key] = [];
      }
      // Flatten array values
      if (Array.isArray(filter.value)) {
        groupedFilters[key].push(...filter.value);
      } else if (filter.value !== null && filter.value !== undefined) {
        groupedFilters[key].push(filter.value);
      }
    });

    // Process grouped filters
    Object.entries(groupedFilters).forEach(([key, values]) => {
      const [field, operator] = key.split(':');

      switch (field) {
        case 'created_at':
        case 'updated_at':
          if (operator === 'between' && values[0]?.from && values[0]?.to) {
            params.set(`${field}_from`, values[0].from);
            params.set(`${field}_to`, values[0].to);
          } else if (operator === 'before' && values[0]) {
            params.set(`${field}_to`, values[0]);
          } else if (operator === 'after' && values[0]) {
            params.set(`${field}_from`, values[0]);
          }
          break;

        case 'subject':
        case 'email_content':
          if (operator === 'contains' && values[0]) {
            params.set(field, values[0]);
          } else if (operator === 'not_contains' && values[0]) {
            params.set(`${field}_not`, values[0]);
          }
          break;

        case 'status':
        case 'priority':
        case 'tags':
          if ((operator === 'in' || operator === 'contains') && values.length > 0) {
            params.set(field, values.join(','));
          } else if ((operator === 'not_in' || operator === 'not_contains') && values.length > 0) {
            params.set(`${field}_not`, values.join(','));
          }
          break;

        case 'assignee_id':
          if (operator === 'unassigned') {
            params.set('assigneeId', 'unassigned');
          } else if (operator === 'me') {
            params.set('assigneeId', 'me');
          } else if (operator === 'equals' && values[0]) {
            params.set('assigneeId', values[0]);
          } else if (operator === 'in' && values.length > 0) {
            params.set('assigneeId', values.join(','));
          } else if (operator === 'not_in' && values.length > 0) {
            params.set('assigneeIdNot', values.join(','));
          }
          break;

        case 'watcher_id':
          if (operator === 'in' && values.length > 0) {
            params.set('watcherId', values.join(','));
          } else if (operator === 'not_in' && values.length > 0) {
            params.set('watcherIdNot', values.join(','));
          }
          break;

        case 'organization_id':
          if (operator === 'in' && values.length > 0) {
            params.set('organizationId', values.join(','));
          } else if (operator === 'not_in' && values.length > 0) {
            params.set('organizationIdNot', values.join(','));
          }
          break;

        case 'from_email':
          if ((operator === 'equals' || operator === 'contains') && values[0]) {
            params.set('fromEmail', values[0]);
          } else if (operator === 'not_contains' && values[0]) {
            params.set('fromEmailNot', values[0]);
          }
          break;

        case 'requester_id':
          if (operator === 'equals' && values[0]) {
            params.set('requesterId', values[0]);
          } else if (operator === 'in' && values.length > 0) {
            params.set('requesterId', values.join(','));
          }
          break;

        case 'sla_status':
          if (operator === 'in' && values.length > 0) {
            params.set('slaStatus', values.join(','));
          } else if (operator === 'not_in' && values.length > 0) {
            params.set('slaStatusNot', values.join(','));
          }
          break;

        case 'ticket_type':
          if (operator === 'in' && values.length > 0) {
            params.set('ticketType', values.join(','));
          } else if (operator === 'not_in' && values.length > 0) {
            params.set('ticketTypeNot', values.join(','));
          }
          break;

        case 'source_type':
          if (operator === 'in' && values.length > 0) {
            params.set('sourceType', values.join(','));
          } else if (operator === 'not_in' && values.length > 0) {
            params.set('sourceTypeNot', values.join(','));
          }
          break;

        case 'updated_by':
          if (operator === 'equals' && values[0]) {
            params.set('updatedBy', values[0]);
          } else if (operator === 'in' && values.length > 0) {
            params.set('updatedBy', values.join(','));
          }
          break;
      }
    });

    // Add sort config
    if (sortConfig) {
      if (sortConfig.sortBy && sortConfig.sortBy !== 'created_at') {
        params.set('sortBy', sortConfig.sortBy);
      }
      if (sortConfig.sortOrder && sortConfig.sortOrder !== 'desc') {
        params.set('sortOrder', sortConfig.sortOrder);
      }
    }

    return params.toString();
  }

  /**
   * Get filter field display name (German)
   */
  getFilterFieldLabel(field: string): string {
    const labels: Record<string, string> = {
      'created_at': 'Ticket-Datum',
      'updated_at': 'Letzter Kontakt',
      'subject': 'Betreff',
      'email_content': 'E-Mail Inhalt',
      'status': 'Status',
      'priority': 'Priorität',
      'tags': 'Tags',
      'assignee_id': 'Zugewiesen an',
      'organization_id': 'Organisation',
      'from_email': 'E-Mail Adresse',
      'requester_id': 'Kunden-ID',
      'sla_status': 'SLA-Status',
      'ticket_type': 'Ticket-Typ',
      'source_type': 'Kanal',
      'updated_by': 'Aktualisiert von'
    };
    return labels[field] || field;
  }

  /**
   * Get operator display name (German)
   */
  getOperatorLabel(operator: string): string {
    const labels: Record<string, string> = {
      'equals': 'Gleich',
      'not_equals': 'Nicht gleich',
      'contains': 'Enthält',
      'not_contains': 'Enthält nicht',
      'in': 'In',
      'not_in': 'Nicht in',
      'between': 'Zwischen',
      'before': 'Vor',
      'after': 'Nach',
      'unassigned': 'Nicht zugewiesen',
      'me': 'Mir zugewiesen'
    };
    return labels[operator] || operator;
  }
}

export default new CustomViewsService();
